const { getElement, getElements } = window.BAO.Utils

/**
 * @typedef {Object} SelectedVariantOption
 *
 * @property {string} label
 * @property {string} value
 */

const selectors = {
  addToCart: '[data-product-add-to-cart]',
  addToCartText: '[data-product-add-to-cart-text]',
  productBIS: '[data-product-bis-form]',
  productBISForm: '[data-product-bis-el="form"]',
  productBISDate: '[data-product-bis-el="date"]',
  form: '[data-product-form]',
  price: '[data-product-price]',
  saleBadge: '[data-product-sale-badge]',
  warranty: '[data-product-warranty]',
  sku: '[data-product-sku]',
  variantIndicator: '[data-product-variant-indicator]',
  variantAvailabilityIndicator: '[data-product-variant-availability-indicator]',
  variantMessageIndicator: '[data-product-variant-message-indicator]',
  variantDetailIndicator: '[data-product-variant-detail-indicator]',
  productMedia: '[data-complementary-product-media]',
  letter: '[data-product-letter]',
  zodiacSign: '[data-product-zodiac-sign]',
  birthstone: '[data-product-birthstone]',
  bundle: '[data-product-bundle]',
  wishlistButton: '[data-product-wishlist]',
  subscriptionButton: '[data-product-subscription]',
  drawerVariantTitle: '[data-product-variant-drawer-title]',
  complementaryProduct: '[data-product-complementary]',
}

class Variant {
  constructor (data) {
    /**
     * @type {ShopifyVariant}
     * @public
     */
    this.data = data
  }

  async setPrice () {
    let productUrl = getElement(
      'complementary-product-variant-radios',
    ).getAttribute('data-product-url')
    if (!productUrl) {
      productUrl = this.url.toString()
    }

    await window.BAO.sectionRenderer
      .fetch(
        'product-price',
        `${productUrl}?variant=${this.data.id}&section_id=product-price`,
      )
      .then((res) => {
        const newDOM = window.BAO.sectionRenderer.convertToDOM(
          res['product-price'],
        )
        const myself = newDOM.querySelector('.shopify-section')

        const complementaryProductContext = getElement(
          selectors.complementaryProduct,
        )

        const priceElements = getElements(selectors.price, {
          context: complementaryProductContext,
        })

        priceElements.forEach((el) => {
          el.innerHTML = myself.innerHTML
        })
      })
  }

  async setSaleBadge () {
    let productUrl = getElement(
      'complementary-product-variant-radios',
    ).getAttribute('data-product-url')
    if (!productUrl) {
      productUrl = this.url.toString()
    }

    await window.BAO.sectionRenderer
      .fetch(
        'product-sale-badge',
        `${this.url.toString()}?variant=${this.data.id}&section_id=product-sale-badge`,
      )
      .then((res) => {
        const newDOM = window.BAO.sectionRenderer.convertToDOM(
          res['product-sale-badge'],
        )
        const myself = newDOM.querySelector('.shopify-section')

        const complementaryProductContext = getElement(
          selectors.complementaryProduct,
        )

        const saleBadgeElements = getElements(selectors.saleBadge, {
          context: complementaryProductContext,
        })

        saleBadgeElements.forEach((el) => {
          el.outerHTML = myself.innerHTML
        })
      })
  }

  setAvailability () {
    const complementaryProductContext = getElement(
      selectors.complementaryProduct,
    )
    getElements(`${selectors.productBIS}`, {
      context: complementaryProductContext,
    }).forEach((el) => {
      el.setAttribute('aria-hidden', this.data.available)
    })

    getElements(selectors.productBISForm, {
      context: complementaryProductContext,
    }).forEach((el) => {
      el.setAttribute('data-variant-id', this.data.id)
      el.setAttribute('aria-hidden', false)
    })

    getElements(selectors.addToCart, {
      context: complementaryProductContext,
    }).forEach((el) => (el.disabled = !this.data.available))

    getElements(selectors.addToCartText, {
      context: complementaryProductContext,
    }).forEach(
      (el) =>
        (el.textContent = this.data.available
          ? window.theme.strings.addToCart
          : window.theme.strings.soldOut),
    )
  }

  updateWarranty (value, option) {
    const complementaryProductContext = getElement(
      selectors.complementaryProduct,
    )
    if (option != null && value != null && option.toLowerCase() !== 'size') {
      const warrantyElement = getElement(selectors.warranty, {
        context: complementaryProductContext,
      })
      const variantColour = value.toLowerCase()

      if (warrantyElement) {
        warrantyElement.setAttribute(
          'aria-hidden',
          !warrantyElement.dataset.colours.includes(variantColour),
        )
      }
    }
  }

  handleVariantRestockDates (variant) {
    if (window.theme.backInStockDates) {
      const hasRestockDate =
        window.theme.backInStockDates[variant.data.id]?.date

      if (!hasRestockDate) return

      const complementaryProductContext = getElement(
        selectors.complementaryProduct,
      )

      getElements(selectors.productBISDate, {
        context: complementaryProductContext,
      }).forEach((el) => {
        el.innerText = hasRestockDate
      })
    }
  }

  updateInput () {
    const complementaryProductContext = getElement(
      selectors.complementaryProduct,
    )
    const inputs = getElements(`form[action*="/cart/add"] [name="id"]`, {
      context: complementaryProductContext,
    })

    inputs.forEach((input) => {
      input.value = this.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  updateColours (name, value) {
    const isAdventHero = getElement('[data-advent-context]')
    if (!isAdventHero) return

    const colours = getElements('[data-variant-radios-el="option"] input')
    colours.forEach((colour) => {
      colour.checked = colour.value === value
    })
  }

  updateMedia (name, value) {
    if (name !== 'Color') return

    const mediaItems = getElements(`${selectors.productMedia}`)

    mediaItems.forEach((el) => {
      el.setAttribute('aria-hidden', el.dataset.colour !== value)
    })

    document.dispatchEvent(new CustomEvent('refreshMediaGallery'))
  }

  updateLetters (name, value) {
    if (name !== 'Color') return

    const variantColour = value.toLowerCase()
    const letters = getElements(selectors.letter)

    letters.forEach((el) => {
      const url = el.getAttribute(`data-colour-${variantColour}-url`)
      el.setAttribute('href', url)

      const availability = el.getAttribute(
        `data-colour-${variantColour}-available`,
      )
      el.setAttribute('aria-disabled', availability !== 'true')
    })
  }

  updateZodiacSigns (name, value) {
    if (name !== 'Color') return

    const variantColour = value.toLowerCase()
    const zodiacSigns = getElements(selectors.zodiacSign)

    zodiacSigns.forEach((el) => {
      const url = el.getAttribute(`data-colour-${variantColour}-url`)
      el.setAttribute('href', url)

      const availability = el.getAttribute(
        `data-colour-${variantColour}-available`,
      )
      el.setAttribute('aria-disabled', availability !== 'true')
    })
  }

  updateBirthstones (name, value) {
    if (name !== 'Color') return

    const variantColour = value.toLowerCase()
    const birthstones = getElements(selectors.birthstone)

    birthstones.forEach((el) => {
      const url = el.getAttribute(`data-colour-${variantColour}-url`)
      el.setAttribute('href', url)

      const availability = el.getAttribute(
        `data-colour-${variantColour}-available`,
      )
      el.setAttribute('aria-disabled', availability !== 'true')
    })
  }

  updateBundles (name, value) {
    if (name !== 'Color') return

    const variantColour = value.toLowerCase()
    const bundles = getElements(selectors.bundle)

    bundles.forEach((el) => {
      const url = el.getAttribute(`data-colour-${variantColour}-url`)
      if (!url) return

      el.setAttribute('href', url)

      const availability = el.getAttribute(
        `data-colour-${variantColour}-available`,
      )
      el.setAttribute('aria-disabled', availability !== 'true')
    })
  }

  updateWishlist (name) {
    if (name !== 'Color') return

    const wishlistButton = getElement(selectors.wishlistButton)

    if (!wishlistButton && !window.iWish && !window.iWishData) return

    wishlistButton.dataset.variant = this.id

    window.iWish.isInWishlist(wishlistButton.dataset.variant)
      ? wishlistButton.classList.add('iwishAdded')
      : wishlistButton.classList.remove('iwishAdded')

    wishlistButton.innerHTML = window.iWish.isInWishlist(
      wishlistButton.dataset.variant,
    )
      ? window.iWishData.iwish_added_txt
      : window.iWishData.iwish_add_txt
  }

  updateSizes (name, value, variants) {
    if (name !== 'Color') return

    const complementaryProductContext = getElement(
      selectors.complementaryProduct,
    )

    const matchingVariants = variants.filter(
      (variant) => variant.option1.toLowerCase() === value.toLowerCase(),
    )
    const matchingOptions = getElements(selectors.drawerVariantTitle, {
      context: complementaryProductContext,
    }).filter((matchingOption) => matchingOption.dataset.option === 'Size')

    matchingVariants.forEach((variant) => {
      matchingOptions.forEach((option) => {
        if (variant.option2 === option.dataset.value) {
          option
            .closest('.prd-ComplementaryVariants_Option-radio')
            .setAttribute(
              'aria-disabled',
              !variant.available ? 'true' : 'false',
            )
          option.innerText = option.dataset.translation
        }
      })
    })
  }

  updateSubscription (name, variant) {
    if (name !== 'Color') return

    const subscriptionButton = getElement(selectors.subscriptionButton)

    if (!subscriptionButton) return

    subscriptionButton.dataset.variant = variant
  }

  setUrl () {
    window.history.replaceState(
      { path: this.url.toString() },
      '',
      this.url.toString(),
    )
  }

  setShareUrl () {
    const shareButtons = getElements('share-button')
    if (shareButtons.length === 0) return

    shareButtons.forEach((shareButton) => {
      shareButton.updateUrl(this.url.toString())
    })
  }

  /**
   * @returns {number}
   */
  get id () {
    return this.data.id
  }

  /**
   * @returns {URL}
   */
  get url () {
    // eslint-disable-next-line compat/compat
    const url = new URL(window.location.href)

    return url
  }
}

export class VariantOptions extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['data', 'option', 'option-choice']
  }

  constructor () {
    super()

    /**
     * @type {Variant[]}
     * @public
     */
    this.collectedVariants = [new Variant(this.defaultVariant)]
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(this, 'change', this.onVariantChange.bind(this))
  }

  onVariantChange (e) {
    const variant = this.getVariant()

    variant.setAvailability()
    variant.handleVariantRestockDates(variant)
    variant.setPrice()
    variant.setSaleBadge()
    variant.updateWarranty(e.target.value, e.target.name)
    variant.updateInput()
    variant.updateSizes(e.target.name, e.target.value, this.data)
    variant.updateColours(e.target.name, e.target.value)
    variant.updateMedia(e.target.name, e.target.value)
    variant.updateLetters(e.target.name, e.target.value)
    variant.updateZodiacSigns(e.target.name, e.target.value)
    variant.updateBirthstones(e.target.name, e.target.value)
    variant.updateBundles(e.target.name, e.target.value)
    variant.updateSubscription(
      e.target.name,
      e.target.dataset.previousEditionVariant,
    )

    if (e.target.name === 'Color') {
      this.dispatchEvent(
        new CustomEvent('colourSwitch', {
          bubbles: true,
          detail: {
            value: e.target.value,
          },
        }),
      )
    }

    if (
      !e.currentTarget.getAttribute('data-form-id').includes('complementary')
    ) {
      setTimeout(() => {
        window.drawers.activeDrawerKey = ''
      }, 450)
    }

    if (!this.currentVariant) {
      this.resetPage(variant)

      return
    }

    variant.setUrl()
    variant.setShareUrl()
  }

  /**
   * @param {Variant} variant
   *
   * @returns {void}
   */
  resetPage (variant) {
    window.history.replaceState(
      { path: window.location.pathname },
      '',
      window.location.pathname,
    )
  }

  /**
   * @param {ShopifyVariant} data
   *
   * @returns {Variant}
   */
  createVariant (data) {
    const variant = new Variant(data)
    this.collectedVariants.push(variant)

    return variant
  }

  /**
   * @returns {Variant}
   */
  getVariant () {
    if (!this.currentVariant) {
      return this.collectedVariants[0]
    }

    const existingVariant = this.collectedVariants.find(
      (variant) => variant.id === this?.currentVariant?.id,
    )
    if (existingVariant) return existingVariant

    return this.createVariant(this.currentVariant)
  }

  /**
   * @returns {SelectedVariantOption[]}
   */
  get selectedOptions () {
    this.throwError(`selectedOptions needs to be implemented on this class`)

    return []
  }

  /**
   * @returns {ShopifyVariant}
   */
  get defaultVariant () {
    return this.data[0]
  }

  /**
   * @returns {ShopifyVariant|undefined}
   */
  get currentVariant () {
    return this.data.find((variant) => {
      return variant.options.every(
        (option, index) => this.selectedOptions[index].value === option,
      )
    })
  }

  /**
   * @returns {ShopifyVariant[]}
   */
  get data () {
    return JSON.parse(this.els.data.element.innerText)
  }
}

export class ComplementaryProductVariantRadios extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map((el) => {
      const { name } = el.dataset
      const choice = getElements(this.els['option-choice'].selector, {
        context: el,
      }).find((el) => el.checked).value

      return {
        label: name,
        value: choice,
      }
    })
  }
}

export class VariantSelects extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map((el) => {
      const { name } = el.dataset

      return {
        label: name,
        value: el.querySelector('select').value,
      }
    })
  }
}

if (!customElements.get('complementary-product-variant-radios')) {
  customElements.define(
    'complementary-product-variant-radios',
    ComplementaryProductVariantRadios,
  )
}

if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', VariantSelects)
}
